import type { Scene, Camera, WebGLRenderer } from 'three';

export type FrameCallback = (dt: number, elapsed: number) => void;

export interface RenderLoopHandle {
  start: () => void;
  stop: () => void;
}

// startLoop will run the animation frame loop and call the provided
// per-frame callback before rendering the given scene with camera.
export default function createRenderLoop(
  renderer: WebGLRenderer,
  scene: Scene,
  camera: Camera,
  onFrame?: FrameCallback
): RenderLoopHandle {
  let rafId: number | null = null;
  let running = false;
  let lastTime = performance.now();
  let elapsed = 0;

  const loop = (now: number) => {
    rafId = requestAnimationFrame(loop);
    const dt = Math.max(0, (now - lastTime) / 1000);
    lastTime = now;
    elapsed += dt;
    try {
      onFrame && onFrame(dt, elapsed);
    } catch (e) {
      // don't let app crash on user code
      // eslint-disable-next-line no-console
      console.error('renderLoop onFrame error', e);
    }
    try {
      renderer.render(scene, camera);
    } catch (e) {
      // rendering errors shouldn't break the loop
      // eslint-disable-next-line no-console
      console.error('renderLoop render error', e);
    }
  };

  return {
    start: () => {
      if (running) return;
      running = true;
      lastTime = performance.now();
      rafId = requestAnimationFrame(loop);
    },
    stop: () => {
      if (!running) return;
      running = false;
      if (rafId !== null) cancelAnimationFrame(rafId);
      rafId = null;
    },
  };
}
